/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { Option } from '@g17eco/molecules/select/SelectFactory';
import { normalizeText } from '@utils/string-format';
import { MergedValueListOption } from '@g17eco/types/initiativeUniversalTracker';

export interface ReactSelectOptions {
  value: string | number;
  label: string | number;
  isDisabled?: boolean;
}

export const getOption = (options: Option<string>[], vd: string, allowCustomOptions?: boolean) => {
  const option = options.find(o => o.value === vd);
  return option ?? { value: vd, label: vd, isDisabled: !allowCustomOptions };
};

export const initCustomOptions = (data: string[], options: Option<string>[], allowCustomOptions?: boolean) => {
  return data.reduce((custom, vd) => {
    if (vd === undefined || vd === '') {
      return custom;
    }

    const option = options.find(o => o.value === vd);
    if (!option) {
      custom.push({
        value: vd,
        label: vd,
        isDisabled: !allowCustomOptions
      });
    }
    return custom;
  }, [...options]);
};

/**
 * Merge subsidiary custom options with base options, filtering out duplicates using case-insensitive comparison
 */
export const mergeCustomOptions = (
  baseOptions: MergedValueListOption[],
  subsidiaryOptions?: MergedValueListOption[],
): MergedValueListOption[] => {
  if (!subsidiaryOptions || subsidiaryOptions.length === 0) {
    return baseOptions;
  }

  const combined = [...baseOptions];
  subsidiaryOptions.forEach((subOpt) => {
    const normalizedSubCode = normalizeText(subOpt.code);
    const isDuplicate = combined.some((baseOpt) => {
      // Custom option has code and name as the same value. So we check if subsidiary code matches either base code or name
      return [normalizeText(baseOpt.code), normalizeText(baseOpt.name)].includes(normalizedSubCode);
    });

    if (!isDuplicate) {
      combined.push(subOpt);
    }
  });

  return combined;
};
